import { useField, useFormikContext } from "formik";
import { BaseTextInputWithLabelProps, TextInputWithLabel } from "./TextInputWithLabel";
import { BaseSelectWithLabelProps, SelectWithLabel, SelectOption } from "./SelectWithLabel";
import { BaseTextAreaWithLabelProps, TextAreaWithLabel } from "./TextAreaWithLabel";
import { BaseDateInputWithLabelProps, DateInputWithLabel } from "./DateInputWithLabel";
import { BaseTimeZonePickerWithLabelProps, TimeZonePickerWithLabel } from "./TimeZonePickerWithLabel";
import { BaseTimePickerWithLabelProps, TimePickerWithLabel } from "./TimePickerWithLabel";

interface BaseFormikInputProps {
  name: string;
  setFieldValue?: (field: string, value: any) => void;
}

interface FormikTextInputProps extends BaseFormikInputProps, Omit<BaseTextInputWithLabelProps, 'name'> {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';
  /** When set, numeric values are displayed with this thousands separator (e.g. ",") */
  thousandsSeparator?: string;
}

interface FormikSelectInputProps extends BaseFormikInputProps, Omit<BaseSelectWithLabelProps, 'value' | 'onChange' | 'name'> {
  type: 'select';
}

interface FormikTextAreaProps extends BaseFormikInputProps, Omit<BaseTextAreaWithLabelProps, 'value' | 'onChange' | 'name'> {
  type: 'textarea';
}

interface FormikDateInputProps extends BaseFormikInputProps, Omit<BaseDateInputWithLabelProps, 'value' | 'onChange' | 'name'> {
  type: 'date';
}

interface FormikTimeZonePickerProps extends BaseFormikInputProps, Omit<BaseTimeZonePickerWithLabelProps, 'value' | 'onChange' | 'name'> {
  type: 'timezone';
}

interface FormikTimePickerProps extends BaseFormikInputProps, Omit<BaseTimePickerWithLabelProps, 'value' | 'timezone' | 'onTimeChange' | 'onTimeZoneChange' | 'name' | 'nameTimezone'> {
  type: 'time';
  nameTimezone: string;
}

type FormikInputProps =
  | FormikTextInputProps
  | FormikSelectInputProps
  | FormikTextAreaProps
  | FormikDateInputProps
  | FormikTimeZonePickerProps
  | FormikTimePickerProps;

const applyThousandsSeparator = (val: string, separator: string): string => {
  if (!val) {
    return "";
  }
  const numeric = val.replace(/[^0-9.]/g, "");
  if (!numeric) {
    return "";
  }
  const parts = numeric.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  return parts.join(".");
};

export const FormikInputWithLabel: React.FC<FormikInputProps> = ({
  name,
  type = 'text',
  setFieldValue: customSetFieldValue,
  ...props
}) => {
  const [field, meta] = useField<string>(name);
  const { setFieldValue, setFieldTouched } = useFormikContext();

  const hasError = !!(meta.error && meta.touched);

  const thousandsSep = 'thousandsSeparator' in props ? props.thousandsSeparator : undefined;

  // Text input handler
  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const setValue = customSetFieldValue || setFieldValue;
    let val = (e.target as HTMLInputElement).value;
    const key = e.keyCode || (e as any).charCode;

    if (thousandsSep && key !== 8) {
      setValue(name, applyThousandsSeparator(val, thousandsSep));
      return;
    }

    if (type !== 'select' && 'formatValue' in props && props.formatValue) {
      if (key === 8) {
        return;
      }
      val = props.formatValue(val);
      setValue(name, val);
    }
  };

  // Select input handler
  const handleSelectChange = (option: SelectOption | null) => {
    const setValue = customSetFieldValue || setFieldValue;
    setValue(name, option ? option.value : "");
  };

  const handleSelectBlur = () => {
    setFieldTouched(name, true);
  };

  // Textarea handler
  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const setValue = customSetFieldValue || setFieldValue;
    setValue(name, e.currentTarget.value);
  };

  // Date input handler
  const handleDateChange = (date: Date | null) => {
    const setValue = customSetFieldValue || setFieldValue;
    setValue(name, date);
  };

  // Timezone picker handler
  const handleTimezoneChange = (timezone: string) => {
    const setValue = customSetFieldValue || setFieldValue;
    setValue(name, timezone);
  };

  // Time picker handlers
  const handleTimeChange = (time: string) => {
    const setValue = customSetFieldValue || setFieldValue;
    setValue(name, time);
  };

  const handleTimeZoneChange = (timezone: string) => {
    const setValue = customSetFieldValue || setFieldValue;
    if ('nameTimezone' in props) {
      setValue(props.nameTimezone, timezone);
    }
  };

  if (type === 'select' && 'options' in props) {
    // Find the current option from the field value
    const currentValue = props.options.find((opt: SelectOption) => opt.value === field.value) || null;

    return (
      <SelectWithLabel
        {...props}
        name={name}
        value={currentValue}
        hasError={hasError}
        errorText={meta.error}
        onChange={handleSelectChange}
        onBlur={handleSelectBlur}
      />
    );
  }

  if (type === 'textarea') {
    return (
      <TextAreaWithLabel
        {...(props as BaseTextAreaWithLabelProps)}
        name={name}
        value={field.value}
        hasError={hasError}
        errorText={meta.error}
        onChange={handleTextAreaChange}
        onBlur={field.onBlur}
      />
    );
  }

  if (type === 'date') {
    let dateValue: Date | null = null;
    if (field.value) {
      const fieldVal = field.value as any;
      if (fieldVal instanceof Date) {
        dateValue = fieldVal;
      } else if (typeof fieldVal === 'string') {
        // Append T00:00:00 to YYYY-MM-DD strings to avoid UTC timezone offset
        // which can shift the date by one day in positive-offset timezones (e.g. AEST)
        const dateStr = fieldVal.match(/^\d{4}-\d{2}-\d{2}$/) ? fieldVal + 'T00:00:00' : fieldVal;
        dateValue = new Date(dateStr);
      } else if (typeof fieldVal === 'number') {
        dateValue = new Date(fieldVal);
      }
    }

    return (
      <DateInputWithLabel
        {...(props as BaseDateInputWithLabelProps)}
        name={name}
        value={dateValue}
        hasError={hasError}
        errorText={meta.error}
        onChange={handleDateChange}
      />
    );
  }

  if (type === 'timezone') {
    return (
      <TimeZonePickerWithLabel
        {...(props as BaseTimeZonePickerWithLabelProps)}
        name={name}
        value={field.value}
        hasError={hasError}
        errorText={meta.error}
        onChange={handleTimezoneChange}
      />
    );
  }

  if (type === 'time' && 'nameTimezone' in props) {
    const [timezoneField] = useField<string>(props.nameTimezone);

    return (
      <TimePickerWithLabel
        {...(props as BaseTimePickerWithLabelProps)}
        name={name}
        nameTimezone={props.nameTimezone}
        value={field.value}
        timezone={timezoneField.value}
        hasError={hasError}
        errorText={meta.error}
        onTimeChange={handleTimeChange}
        onTimeZoneChange={handleTimeZoneChange}
      />
    );
  }

  // Default to text/email/password/etc input
  const displayValue = thousandsSep
    ? applyThousandsSeparator(field.value, thousandsSep)
    : field.value;

  return (
    <TextInputWithLabel
      {...field}
      {...(props as BaseTextInputWithLabelProps)}
      type={type as any}
      name={name}
      value={displayValue}
      hasError={hasError}
      errorText={meta.error}
      onKeyUp={onKeyUp}
    />
  );
};